/**
 * Error Boundary Component
 *
 * Catches React errors and displays a user-friendly fallback UI.
 */
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Bug } from 'lucide-react';
import { api } from '../services/api';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ errorInfo });

    // Log to error tracking service
    this.logError(error, errorInfo);
  }

  logError(error: Error, errorInfo: ErrorInfo) {
    // E.5 (2026-04-29): post-A.10 Mode 1 OIDC PKCE — backend AuthMiddleware
    // enforces JWT on /api/errors. Route through `api.post()` so the request
    // carries `Authorization: Bearer <token>` from
    // `localStorage['vibe_designer_token']`. Fire-and-forget — `.catch()`
    // keeps logError from itself triggering a fresh ErrorBoundary on rejection.
    api.post('/errors', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    }).catch(console.error);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center h-full bg-vibe-bg p-8">
          <AlertTriangle className="w-16 h-16 text-vibe-error mb-4" />
          <h2 className="text-xl font-semibold text-vibe-text mb-2">
            Something went wrong
          </h2>
          <p className="text-vibe-text-secondary text-center max-w-md mb-6">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>

          <div className="flex gap-4">
            <button
              onClick={this.handleRetry}
              className="flex items-center gap-2 px-4 py-2 bg-vibe-accent text-white rounded hover:bg-vibe-accent-hover"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>

            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 px-4 py-2 bg-vibe-bg-tertiary text-vibe-text rounded hover:bg-vibe-border"
            >
              Reload Page
            </button>
          </div>

          {import.meta.env.DEV && this.state.errorInfo && (
            <details className="mt-8 w-full max-w-2xl">
              <summary className="flex items-center gap-2 cursor-pointer text-vibe-text-secondary hover:text-vibe-text">
                <Bug className="w-4 h-4" />
                Developer Details
              </summary>
              <pre className="mt-2 p-4 bg-vibe-bg-tertiary rounded text-xs overflow-auto max-h-64">
                {this.state.error?.stack}
                {'\n\nComponent Stack:\n'}
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
